import {
  Alert,
  Box,
  BoxContent,
  BoxSidebar,
  BoxSidebarItem,
  Button,
  Heading,
  Link,
  SearchInput,
  TextLink,
} from '@mm/components';
import { selectChatStartersCountByUiFilterKind } from '@mm/state/selectors/chatStarters';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { showInstaChampAlertSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectInstagramAccountId } from '@mm/state/selectors/instagramSelectors';
import { hasSMSNumberSelector } from '@mm/state/selectors/twillio';
import { hasConnectedRealFacebookPageSelector } from '@mm/state/selectors/userSelector';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ButtonColor } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { PaginationNav } from '../../../../../common/components/PaginationNav/PaginationNav';
import { TooltipGuard } from '../../../../../modules/permissions/RequiresPermissions';
import { ActivateIGBanner } from './ActivateIGBanner/ActivateIGBanner';
import { ActivateIGStoryMentionsBanner } from './ActivateIGStoryMentionsBanner/ActivateIGStoryMentionsBanner';
import { ActivateSmsBanner } from './ActivateSmsBanner/ActivateSmsBanner';
import './ChatStarters.pcss';
import { ChatStartersEmptyState } from './ChatStartersEmptyState/ChatStartersEmptyState';
import { ConnectPageBanner } from './ConnectPageBanner/ConnectPageBanner';
import { CreateChatStarterButton } from './CreateChatStarterButton/CreateChatStarterButton';
import { StarterFilterByKind } from './StarterFilterByKind/StarterFilterByKind';
import { StartersTable } from './StartersTable/StartersTable';
import { facebookRelatedStarterKinds } from './types';
import { useChatStarters } from './use-chat-starters';

type Props = {
  className?: string;
};

// tslint:disable:cyclomatic-complexity
export const ChatStarters = ({ className }: Props) => {
  const {
    activeKind,
    setFilterKind,
    starterRecords,
    changePage,
    activeSearch,
    setSearchQuery,
  } = useChatStarters();
  const count = useSelector((state: StoreState) =>
    selectChatStartersCountByUiFilterKind(state, activeKind),
  );
  const facebookPage = useSelector(activeFacebookPageSelector);

  const hasTwilioNumber = useSelector(hasSMSNumberSelector);
  const instagramAccountId = useSelector(selectInstagramAccountId);

  const starters = starterRecords?.chatStarters;
  const isEmpty = !starterRecords?.pagination?.total;

  const pagination = starterRecords?.pagination;
  const showSMSEmptyState =
    ['sms_keywords', 'optin_popups'].includes(activeKind) && !hasTwilioNumber;

  const showIGWelcomerEmptyState =
    activeKind === 'instagram_welcomer' && !instagramAccountId;

  const showIGStoryMentionsEmptyState =
    activeKind === 'instagram_story_mention' && !instagramAccountId;

  const isInstagramCommentGuard = activeKind === 'instagram_comment_guard';

  const showIGCommentGuardEmptyState =
    isInstagramCommentGuard && !instagramAccountId;

  const [showInstaChampAlert, instagramUsername] = useSelector(
    showInstaChampAlertSelector,
  );
  const isInstagramChatStarter = [
    'instagram_welcomer',
    'instagram_story_mention',
    'instagram_comment_guard',
  ].includes(activeKind);

  const igStartersEmptyState =
    showIGWelcomerEmptyState ||
    showIGStoryMentionsEmptyState ||
    showIGCommentGuardEmptyState;

  const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const [alertVisible, setAlertVisible] = useState(
    !!facebookPage?.euLimitsAt &&
      (activeKind === 'landing_pages' ||
        activeKind === 'facebook_optin' ||
        activeKind === 'share_links'),
  );

  useEffect(() => {
    setAlertVisible(
      !!facebookPage?.euLimitsAt &&
        (activeKind === 'landing_pages' ||
          activeKind === 'facebook_optin' ||
          activeKind === 'share_links'),
    );
  }, [activeKind, !!facebookPage?.euLimitsAt]);

  const onCloseAlert = () => {
    setAlertVisible(false);
  };

  const createDisabled = !hasTwilioNumber && showSMSEmptyState;

  const boxContentClassName = classNames({
    'chat-starters__box-content---with-warning': alertVisible,
  });

  const facebookPreCreatedKind =
    facebookRelatedStarterKinds.includes(activeKind);

  const isUserPageConnected = useSelector(hasConnectedRealFacebookPageSelector);

  return (
    <section className={classNames(className, 'chat-starters')}>
      <Box className="chat-starters__box">
        <BoxSidebar className="chat-starters__box-sidebar">
          <BoxSidebarItem className="chat-starters__sidebar-filter">
            <Heading
              className="chat-starters__filter-heading"
              text="View by type"
              size={5}
            />
            <StarterFilterByKind
              activeKind={activeKind}
              onChange={setFilterKind}
            />
          </BoxSidebarItem>
        </BoxSidebar>
        <BoxContent className={boxContentClassName}>
          <div className="chat-starters__content-topbar">
            <SearchInput
              css={{ container: 'chat-starters__search-input' }}
              placeholder="Search"
              value={activeSearch}
              onChange={onSearchInputChange}
            />
            <div />
            <CreateChatStarterButton
              className="chat-starters__create-starter-button"
              starterKind={activeKind}
              color={
                igStartersEmptyState ? ButtonColor.green : ButtonColor.blue
              }
              prepend="Create"
              withPlusIcon
              disabled={createDisabled}
              buttonText={
                igStartersEmptyState ? 'Connect Instagram' : undefined
              }
            />
          </div>
          {isInstagramChatStarter && showInstaChampAlert && instagramAccountId && (
            <Alert type="warning" showCloseIcon={false}>
              <div>
                This Instagram account was connected via InstaChamp. Chat
                Starters changed in Customers.ai may be blocked from making
                further changes in InstaChamp.
              </div>
              <Button
                onClick={() => {
                  window.location.href = `/instachamp/@${instagramUsername}`;
                }}
              >
                Edit with InstaChamp
              </Button>
            </Alert>
          )}
          {alertVisible && (
            <Alert
              type="warning"
              className="chat-starters__warning-alert"
              closeIconProps={{ onClick: onCloseAlert }}
            >
              This feature is no longer available on Messenger due to{' '}
              <Link
                href="https://customers.ai/help/article/cg1qvuexfy-updated-messaging-policies-for-eu-pages-users"
                isBlank
              >
                EU policy changes.
              </Link>
            </Alert>
          )}
          <div className="chat-starters__content-data">
            {!isUserPageConnected && <ConnectPageBanner kind={activeKind} />}
            {showSMSEmptyState && <ActivateSmsBanner />}
            {showIGWelcomerEmptyState && <ActivateIGBanner />}
            {showIGCommentGuardEmptyState && (
              <ActivateIGBanner title="Create a Dialogue that will interact with contacts who leaves a comment on your post" />
            )}
            {showIGStoryMentionsEmptyState && <ActivateIGStoryMentionsBanner />}
            {isEmpty && !igStartersEmptyState && (
              <ChatStartersEmptyState
                starterKind={activeKind}
                createDisabled={createDisabled}
              />
            )}
            {starters && (
              <TooltipGuard guardIf={facebookPreCreatedKind}>
                {isAllowed => (
                  <StartersTable
                    className={classNames({
                      'chat-starters__table--disabled': !isAllowed,
                    })}
                    starters={starters}
                  />
                )}
              </TooltipGuard>
            )}
          </div>

          <div className="chat-starters__content-footer">
            {pagination && (
              <PaginationNav
                pagination={pagination}
                onChangePage={changePage}
                rangeLength={5}
                circularActive
                withEllipsis
              />
            )}
            {Boolean(count) && (
              <TextLink className="chat-starters__total">
                Found{' '}
                <span className="chat-starters__total-count">{count}</span>{' '}
                items
              </TextLink>
            )}
          </div>
        </BoxContent>
      </Box>
    </section>
  );
};
